import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, HelpCircle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Field, NumberInput, Chip } from "../common";
import { AnnotationEditor, emptyAnnotation } from "./AnnotationEditor";
import { MatchPanel } from "./MatchPanel";

const PRINTED = [["del_pckgs", "Colis livrés"], ["del_stops", "Stops livrés"], ["pu_pckgs", "Colis enlevés"], ["pu_stops", "Stops enlevés"]];
export const QUESTION_TEMPLATES = [
  "Cette mention indique-t-elle 2 ou 3 Express+ ?",
  "Les stops indiqués sont-ils les stops normaux en plus des Express+, ou le total de la prestation ?",
  "Ces palettes sont-elles livrées ou enlevées, et sur combien de stops ?",
  "Cette annotation concerne-t-elle le rapport de cette page ou celui de la page précédente ?",
  "À quel sous-traitant cette prestation doit-elle être attribuée ?",
  "Quels sont les kilomètres et la durée de ce Spécial ?",
];

const toForm = (r) => ({
  ...r, printed: Object.fromEntries(PRINTED.map(([k]) => [k, r.printed?.[k] ?? ""])),
  annotations: (r.annotations || []).map((a) => ({ ...a, components: a.components.map((c) => ({ ...c,
    qty: Object.fromEntries(Object.entries(c.qty).map(([k, v]) => [k, v ?? ""])),
    special: c.special ? { ...c.special, km: c.special.km ?? "", hours: c.special.minutes != null ? Math.floor(c.special.minutes / 60) : "", minutes: c.special.minutes != null ? c.special.minutes % 60 : "" }
      : { destination: "", address: "", km: "", hours: "", minutes: "", kind: "", origin: "" } })) })),
});

const toBody = (f) => ({
  file_id: f.file_id, page_no: f.page_no, company_id: f.company_id || null, work_date: f.work_date || "", employee_id: f.employee_id || "",
  route_id: f.route_id || "", printed: f.printed, filename_hint: f.filename_hint || "", hint_conflict: !!f.hint_conflict,
  annotations: f.annotations.map((a) => ({ ...a, components: a.components.map((c) => {
    const sp = c.special || {};
    const mins = sp.hours === "" && sp.minutes === "" ? null : Number(sp.hours || 0) * 60 + Number(sp.minutes || 0);
    return { ...c, special: { ...sp, km: sp.km === "" ? null : Number(sp.km), minutes: mins } };
  }) })),
});

function Questions({ r, onChanged }) {
  const [text, setText] = useState("");
  const [ann, setAnn] = useState("");
  const add = async () => {
    try { await api.post(`/pm/reports/${r.id}/questions`, { text, annotation_id: ann || null }); setText(""); toast.success("Question ajoutée au lot"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      {(r.questions || []).map((q) => (
        <div key={q.id} className={`text-sm rounded-lg p-2 ${q.status === "answered" ? "bg-slate-50" : "bg-amber-50"}`} data-testid={`question-${q.id}`}>
          <HelpCircle size={14} className="inline mr-1" />{q.text}
          {q.status === "answered" ? <div className="text-xs mt-1">Réponse : <b>{q.answer}</b>{q.reason && ` — motif : ${q.reason}`} ({q.answered_by})</div> : <Chip tone="amber">En attente</Chip>}
        </div>
      ))}
      <div className="grid sm:grid-cols-[1fr_180px_auto] gap-2">
        <input className="tl-input" list={`qt-${r.id}`} placeholder="Question précise…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`question-text-${r.id}`} />
        <datalist id={`qt-${r.id}`}>{QUESTION_TEMPLATES.map((q) => <option key={q} value={q} />)}</datalist>
        <select className="tl-select" value={ann} onChange={(e) => setAnn(e.target.value)} data-testid={`question-ann-${r.id}`}>
          <option value="">Tout le rapport</option>
          {(r.annotations || []).map((a, i) => <option key={a.id} value={a.id}>Annotation {i + 1}</option>)}
        </select>
        <button className="tl-btn tl-btn-ghost" onClick={add} disabled={!text.trim()} data-testid={`question-add-${r.id}`}><Plus size={14} /> Question</button>
      </div>
    </div>
  );
}

export function ReportCard({ r, idx, companies, lexicon, onChanged }) {
  const [f, setF] = useState(toForm(r));
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setF(toForm(r)); setDirty(false); }, [r]);
  const set = (patch) => { setF({ ...f, ...patch }); setDirty(true); };
  const save = async () => {
    try { await api.patch(`/pm/reports/${r.id}`, toBody(f)); toast.success("Rapport enregistré"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer ce rapport et ses annotations ?")) return;
    try { await api.delete(`/pm/reports/${r.id}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const validate = async (a) => {
    try {
      const { data } = await api.post(`/pm/reports/${r.id}/annotations/${a.id}/validate`);
      toast.success(data.applied ? `Annotation validée — ${data.ref} mise à jour (UPS ${data.calc.ups ?? "?"} € · sous-traitant ${data.calc.sub ?? "?"} €)` : "Annotation validée (sera appliquée au rapprochement)");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const lexHits = lexicon.filter((x) => f.annotations.some((a) => a.text_read.toLowerCase().includes(x.wrong.toLowerCase())));
  return (
    <div className="tl-card space-y-3 border-l-4 border-slate-800" data-testid={`report-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-bold uppercase">Rapport {idx + 1}</h4>
        {r.source === "ia" && <Chip tone="blue" testid={`report-ai-${idx}`}>Proposé par l'IA — à vérifier</Chip>}
        {r.open_items?.length ? <Chip tone="amber" testid={`report-open-${idx}`}>{r.open_items.length} point(s) ouvert(s)</Chip> : <Chip tone="green" testid={`report-open-${idx}`}>Complet</Chip>}
        <button className="ml-auto tl-btn tl-btn-danger tl-btn-sm" onClick={remove} aria-label="Supprimer le rapport" data-testid={`report-delete-${idx}`}><Trash2 size={14} /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Field label="Sous-traitant (à valider)" className="col-span-2">
          <select className="tl-select" value={f.company_id || ""} onChange={(e) => set({ company_id: e.target.value })} data-testid={`report-company-${idx}`}>
            <option value="">— À attribuer —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Date de prestation"><input type="date" className="tl-input" value={f.work_date || ""} onChange={(e) => set({ work_date: e.target.value })} data-testid={`report-date-${idx}`} /></Field>
        <Field label="Employee ID"><input className="tl-input tl-mono" value={f.employee_id || ""} onChange={(e) => set({ employee_id: e.target.value })} data-testid={`report-employee-${idx}`} /></Field>
        <Field label="Route ID"><input className="tl-input tl-mono" value={f.route_id || ""} onChange={(e) => set({ route_id: e.target.value })} data-testid={`report-route-${idx}`} /></Field>
        {PRINTED.map(([k, l]) => <Field key={k} label={`${l} (imprimé)`}><NumberInput value={f.printed[k]} onChange={(v) => set({ printed: { ...f.printed, [k]: v } })} testid={`report-printed-${k}-${idx}`} /></Field>)}
        <Field label="Indice du nom de fichier" className="col-span-2"><input className="tl-input" value={f.filename_hint || ""} onChange={(e) => set({ filename_hint: e.target.value })} placeholder="ex. 4 Express+" data-testid={`report-hint-${idx}`} /></Field>
        <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.hint_conflict} onChange={(e) => set({ hint_conflict: e.target.checked })} data-testid={`report-hint-conflict-${idx}`} /> Le manuscrit contredit le nom du fichier (le manuscrit est retenu)</label>
      </div>
      {lexHits.length > 0 && <div className="text-xs bg-blue-50 rounded-lg p-2" data-testid={`report-lexicon-${idx}`}>Lexique : {lexHits.map((x) => `« ${x.wrong} » → ${x.right}`).join(" · ")} (la transcription d'origine est conservée)</div>}
      {f.annotations.map((a, i) => (
        <AnnotationEditor key={a.id || `new-${i}`} a={a} idx={i} saved={!dirty} onValidate={() => validate(a)}
          onChange={(na) => set({ annotations: f.annotations.map((x, j) => (j === i ? na : x)) })}
          onRemove={() => set({ annotations: f.annotations.filter((_, j) => j !== i) })} />
      ))}
      <div className="flex gap-2 flex-wrap">
        <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => set({ annotations: [...f.annotations, emptyAnnotation()] })} data-testid={`report-add-annotation-${idx}`}><Plus size={14} /> Annotation</button>
        <button className="tl-btn tl-btn-primary tl-btn-sm ml-auto" onClick={save} disabled={!dirty} data-testid={`report-save-${idx}`}><Save size={14} /> Enregistrer</button>
      </div>
      {dirty && <p className="text-xs text-amber-700">Enregistrez avant de valider une annotation.</p>}
      <Questions r={r} onChanged={onChanged} />
      <MatchPanel r={r} onChanged={onChanged} />
    </div>
  );
}
